// src/pages/SharedDecks.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import axios from "axios";
import { showToast } from "../components/utils/toast";
import { copyToClipboard } from "../components/utils/copyToClipboard";
import { encode } from "marvel-snap-deckstrings";

function SharedDecks({ user }) {
  const [sharedDecks, setSharedDecks] = useState([]);
  const [cardData, setCardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);

  // snap-deck style modal grid
  const [cardGridOpen, setCardGridOpen] = useState(false);
  const [popupDeck, setPopupDeck] = useState(null);

  // card backs like SnapDeckGroup
  const [cardBacks, setCardBacks] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  const API = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const apply = () => setIsMobile(mq.matches);
    apply();

    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);

  // fetch all card backs once
  useEffect(() => {
    axios
      .get(`${API}/api/data/snapCardBacks`)
      .then((res) => setCardBacks(res.data || []))
      .catch((err) => {
        console.warn(
          "Could not fetch card backs",
          err.response?.status ?? err.message
        );
      });
  }, [API]);

  // build id -> cardBack row map
  const cardBackById = useMemo(() => {
    const map = new Map();
    for (const cb of cardBacks) map.set(Number(cb.id), cb);
    return map;
  }, [cardBacks]);

  // Same normalization logic as MyDecks so deckstrings actually work.
  function camelToWords(str) {
    if (!str) return "";
    return str
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
      .trim();
  }

  function normalizeToDbFormat(str) {
    const merf = camelToWords(str)
      .replace("-", " ")
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join("")
      .replace("HawkeyeKateBishop", "KateBishop")
      .replace("JuggernautHorseman", "Juggernaut,HorsemanOfWar")
      .replace("SamWilsonCaptainAmerica", "SamWilson")
      .replace("DoctorDoom2099", "Doom2099")
      .replace("AranA", "Arana")
      .replace("MisterNegative", "MrNegative")
      .replace("OmegaSentinel", "KarimaShapandar")
      .replace("StarlordMasterOfTheSun", "StarLordMasterOfTheSun")
      .replace("ShouLaoTheUndying", "ShouLao")
      .replace("VivVision", "Viv")
      .replace("MisterFantastic", "MrFantastic")
      .replace("MODOK", "Modok")
      .replace("BatrocTheLeaper", "Batroc")
      .replace("RonanTheAccuser", "Ronan")
      .replace("TheHood", "Hood")
      .replace("Mbaku", "MBaku")
      .replace("TheLivingTribunal", "LivingTribunal")
      .replace("DoctorDoom", "DrDoom")
      .replace("PhoenixForce", "ThePhoenixForce")
      .replace("GorrTheGodButcher", "Gorr")
      .replace("TheInfinaut", "Infinaut")
      .replace("StarLord", "Starlord")
      .replace("MisterSinister", "MrSinister")
      .replace("TheFallenOne", "FallenOne")
      .replace("JaneFosterMightyThor", "JaneFoster")
      .replace("JoaquinTorresFalconIi", "JoaquinTorres")
      .replace("MisterFantasticFirstSteps", "MrFantasticFirstSteps")
      .replace("Modok", "M.o.d.o.k.")
      .replace("UatuTheWatcher", "Uatu");
    return merf;
  }

  async function copyDeckCode(deck) {
    if (!deck) return;

    const ids = [];
    for (let i = 1; i <= 12; i++) {
      const cardId = deck[`card${i}`];
      if (cardId) ids.push(Number(cardId));
    }

    if (ids.length !== 12) {
      showToast("Deck must have 12 cards to copy.", "error");
      return;
    }

    const names = ids
      .map((id) => cardData?.[id]?.cardname)
      .filter(Boolean)
      .map(normalizeToDbFormat);

    if (names.length !== 12) {
      showToast("Missing card data — refresh and try again.", "error");
      return;
    }

    const deckstring = encode({ cards: names });
    const ok = await copyToClipboard(deckstring);
    if (ok) showToast("Deck copied!");
    else showToast("Copy failed", "error");
  }

  const openCardGrid = (deck) => {
    setPopupDeck(deck);
    setCardGridOpen(true);
  };

  const closeCardGrid = () => {
    setCardGridOpen(false);
    setPopupDeck(null);
  };

  useEffect(() => {
    const fetchSharedDecks = async () => {
      if (!user) return;

      setLoading(true);
      setError("");

      try {
        const res = await axios.get(`${API}/api/data/sharedDecks/${user.id}`);
        const decks = res.data || [];
        setSharedDecks(decks);

        const allIds = new Set();
        decks.forEach((deck) => {
          for (let i = 1; i <= 12; i++) {
            const cardId = deck[`card${i}`];
            if (cardId) allIds.add(cardId);
          }
        });

        const requests = Array.from(allIds).map((id) =>
          axios
            .get(`${API}/api/data/snapCards/${id}`, {
              params: { userId: user.id },
            })
            .then((r) => [id, r.data])
            .catch((err) => {
              console.warn("Could not fetch card", id, err?.response?.status);
              return null;
            })
        );

        const results = await Promise.all(requests);
        const lookup = {};
        results.forEach((entry) => {
          if (!entry) return;
          const [id, data] = entry;
          lookup[id] = data;
        });
        setCardData(lookup);
      } catch (err) {
        console.error("Error fetching shared decks:", err);
        setError("Unable to load shared decks.");
      } finally {
        setLoading(false);
      }
    };

    fetchSharedDecks();
  }, [user, API]);

  if (loading) {
    return (
      <>
        <br />
        <br />
        <br />
        <p style={{ textAlign: "center", color: "#ccc" }}>
          Loading shared decks…
        </p>
      </>
    );
  }

  return (
    <div className="list-container">
      <h1 className="main-title" style={{ textAlign: "center" }}>
        Shared Decks
      </h1>

      {error ? (
        <p style={{ textAlign: "center", color: "#ff8a8a" }}>{error}</p>
      ) : null}

      {sharedDecks.length === 0 ? (
        <p style={{ textAlign: "center", color: "#aaa" }}>
          No shared decks available.
        </p>
      ) : (
        <div
          className="tier-grid"
          style={{
            width: "100%",
            display: "grid",
            gap: "14px",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            alignItems: "stretch",
            justifyItems: "stretch",
            marginTop: "18px",
          }}
        >
          {sharedDecks.map((deck) => {
            const snapCardBackId =
              deck.snapcardbackid ??
              deck.snapCardBackId ??
              deck.snap_card_back_id ??
              null;

            const cardBackRow =
              snapCardBackId != null
                ? cardBackById.get(Number(snapCardBackId))
                : null;

            const cardBackSrc =
              cardBackRow?.cardbackhtml ||
              cardBackRow?.cardBackHTML ||
              cardBackRow?.cardbackHTML ||
              null;

            return (
              <div
                key={deck.id}
                className="hof-deck"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  minWidth: 0,
                }}
              >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      className="hof-deck-name"
                      style={{
                        paddingLeft: 0,
                        fontSize: "22px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={deck.deckname}
                    >
                      {deck.deckname}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginTop: "6px",
                      }}
                    >
                      <span className="hof-badge">v{deck.versionnum || "-"}</span>
                      <span className="hof-badge">
                        {deck.deckrank || "Unranked"}
                      </span>
                      <span className="hof-badge" title="Shared by">
                        {deck.username || deck.userName || "Unknown"}
                      </span>
                    </div>
                  </div>

                  {/* Cardback preview (right) */}
                  <div
                    className="deck-card"
                    onClick={() => openCardGrid(deck)}
                    title="Click to view deck cards"
                    style={{
                      width: "110px",
                      height: "140px",
                      cursor: "pointer",
                      border: "1px solid rgba(197, 176, 55, 0.35)",
                      background: "rgba(0,0,0,0.35)",
                    }}
                  >
                    {cardBackSrc ? (
                      <img
                        src={cardBackSrc}
                        alt="Card Back"
                        className="card-hover-img"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#777",
                          fontSize: "0.85rem",
                          textAlign: "center",
                          padding: "8px",
                        }}
                      >
                        No Card Back
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                  }}
                >
                  <button className="button" onClick={() => copyDeckCode(deck)}>
                    Copy Code
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Deck Cards modal (same layout as SnapDeckGroup) */}
      {cardGridOpen && popupDeck && (
        <div
          className="modal-overlay"
          onClick={closeCardGrid}
          style={{
            padding: isMobile ? "10px" : "18px",
            boxSizing: "border-box",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: isMobile ? "100%" : "min(960px, 96vw)",
              maxWidth: "960px",
              padding: isMobile ? "14px" : "18px",
              boxSizing: "border-box",
              maxHeight: isMobile ? "88vh" : "85vh",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              borderRadius: "12px",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: isMobile ? "stretch" : "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                <div
                  style={{
                    color: "rgb(197, 176, 55)",
                    fontWeight: 900,
                    fontSize: isMobile ? "1.05rem" : "1.2rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={popupDeck.deckname}
                >
                  {popupDeck.deckname}
                </div>

                <div
                  style={{
                    color: "#aaa",
                    fontSize: "0.85rem",
                    marginTop: "4px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  v{popupDeck.versionnum || "-"} •{" "}
                  {popupDeck.deckrank || "Unranked"} • Shared by{" "}
                  {popupDeck.username || popupDeck.userName || "Unknown"}
                </div>
              </div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button className="button" onClick={() => copyDeckCode(popupDeck)}>
                  Copy Code
                </button>
                <button
                  className="button"
                  onClick={closeCardGrid}
                  style={{
                    width: isMobile ? "100%" : "auto",
                    padding: isMobile ? "10px 12px" : undefined,
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div
              style={{
                marginTop: "14px",
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(3, minmax(0, 1fr))" // mobile: 3x4
                  : "repeat(6, minmax(0, 1fr))", // desktop: 6x2
                gap: isMobile ? "8px" : "10px",
                width: "100%",
              }}
            >
              {Array.from({ length: 12 }).map((_, i) => {
                const cardId = popupDeck?.[`card${i + 1}`];
                const card = cardId != null ? cardData?.[cardId] : null;

                return card ? (
                  <img
                    key={i}
                    src={
                      card.customhtml && card.customhtml.trim() !== ""
                        ? card.customhtml
                        : card.cardhtml
                    }
                    alt={card.cardname}
                    onClick={() => setSelectedCard(card)}
                    className="card-hover-img deck-card-img"
                    style={{
                      width: "100%",
                      aspectRatio: "2 / 3",
                      borderRadius: "8px",
                      cursor: "pointer",
                      objectFit: "cover",
                    }}
                    title={card.cardname}
                  />
                ) : (
                  <div
                    key={i}
                    style={{
                      width: "100%",
                      aspectRatio: "2 / 3",
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      fontSize: "0.75rem",
                    }}
                  >
                    Empty
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Card details modal (same as before) */}
      {selectedCard && (
        <div className="modal-overlay" onClick={() => setSelectedCard(null)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(720px, 92vw)" }}
          >
            <h2
              className="modal-title"
              style={{ textAlign: "center", marginBottom: "16px" }}
            >
              {selectedCard.cardname}
            </h2>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              <img
                src={
                  selectedCard.customhtml && selectedCard.customhtml.trim() !== ""
                    ? selectedCard.customhtml
                    : selectedCard.cardhtml
                }
                alt={selectedCard.cardname}
                className="modal-card-image"
                style={{
                  maxWidth: "min(320px, 80vw)",
                  borderRadius: "10px",
                  boxShadow: "0 0 10px rgba(255, 215, 0, 0.3)",
                }}
              />
            </div>

            <p
              style={{
                color: "#ddd",
                textAlign: "center",
                fontSize: "1rem",
                marginBottom: "16px",
                wordBreak: "break-word",
              }}
            >
              {selectedCard.carddesc}
            </p>

            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button className="button" onClick={() => setSelectedCard(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SharedDecks;
